import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ExternalLink, Search } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { SEOHead } from "@/components/SEOHead";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { research, medicaleseResearch, ResearchType } from "@/data/research";

const ALL = "All";
const TYPES: (typeof ALL | ResearchType)[] = [
  ALL,
  "Article",
  "Op-ed",
  "Essay",
  "Analysis",
];

const ResearchPage = () => {
  const { pathname } = useLocation();
  const items = useMemo(() => pathname === "/articles" ? [medicaleseResearch, ...research] : research, [pathname]);
  const [activeType, setActiveType] = useState<string>(ALL);
  const [query, setQuery] = useState("");

  const counts = useMemo(() => {
    const c: Record<string, number> = { [ALL]: items.length };
    for (const item of items) c[item.type] = (c[item.type] ?? 0) + 1;
    return c;
  }, [items]);

  const visibleTypes = useMemo(
    () => TYPES.filter((t) => t === ALL || (counts[t] ?? 0) > 0),
    [counts],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      if (activeType !== ALL && item.type !== activeType) return false;
      if (!q) return true;
      return (
        item.title.toLowerCase().includes(q) ||
        item.summary.toLowerCase().includes(q) ||
        item.venue.toLowerCase().includes(q)
      );
    });
  }, [activeType, query, items]);

  return (
    <PublicLayout>
      <SEOHead
        title="Research and Work · David Isei"
        description="Articles, op-eds, and analysis on Hawaiʻi healthcare workforce, network adequacy, behavioral health, and rural access. Every figure carries a source, a retrieval date, and a label."
        url={`https://davidisei.com${pathname === "/articles" ? "/articles" : "/research"}`}
      />

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-liberty-gold">
            Research
          </p>
          <h1 className="mt-3 font-display text-4xl font-bold leading-tight text-foreground sm:text-5xl">
            Research and Work
          </h1>
          <p className="mt-5 text-base leading-relaxed text-foreground/80">
            Every piece here rests on figures that carry a source, a retrieval
            date, and a label. Where a figure has changed since publication, the
            piece carries a note and the Corrections Log has the current value.
          </p>

          <div className="mt-8 space-y-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by title, summary, or venue"
                aria-label="Search research"
                className="h-10 bg-card/40 pl-9"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {visibleTypes.map((t) => {
                const isActive = activeType === t;
                return (
                  <button
                    key={t}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => setActiveType(t)}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                      isActive
                        ? "border-liberty-gold bg-liberty-gold/15 text-liberty-gold"
                        : "border-border/60 text-muted-foreground hover:border-border hover:text-foreground",
                    )}
                  >
                    <span>{t}</span>
                    <span className="rounded-full bg-muted/60 px-1.5 py-0.5 text-[10px] tabular-nums">
                      {counts[t] ?? 0}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <ul className="mt-10 space-y-5">
            {filtered.length === 0 && (
              <li className="py-12 text-center text-sm text-muted-foreground">
                No pieces match your filters.
              </li>
            )}
            {filtered.map((item) => {
              const meta = (
                <>
                  <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                    <span className="font-mono">{item.type}</span>
                    <span>{item.venue}</span>
                    <span>{item.published}</span>
                    {item.external && (
                      <span className="inline-flex items-center gap-1 text-liberty-gold">
                        <ExternalLink className="h-3 w-3" /> external
                      </span>
                    )}
                  </div>
                  <h2 className="mt-3 font-display text-xl font-bold leading-snug text-foreground">
                    {item.title}
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {item.summary}
                  </p>
                  {item.registryNote && (
                    <p className="mt-3 rounded-lg border border-amber-500/50 bg-amber-500/5 p-3 text-[13px] leading-relaxed text-amber-400">
                      {item.registryNote}
                    </p>
                  )}
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-liberty-gold">
                    Read <ArrowUpRight className="h-4 w-4" />
                  </span>
                </>
              );

              return (
                <li
                  key={item.id}
                  className="rounded-xl border border-border/60 bg-card/60 p-5 transition-colors hover:border-liberty-gold/50 sm:p-6"
                >
                  {item.external ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      {meta}
                    </a>
                  ) : (
                    <Link to={item.url} className="block">
                      {meta}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="mt-12 rounded-xl border border-border/60 bg-card/40 p-6">
            <h2 className="font-display text-xl font-bold text-foreground">
              Task Force publications
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground/80">
              Institutional pieces published in the Task Force's voice, many with
              my analysis behind them, are at{" "}
              <a
                href="https://hawaiihealthcaretaskforce.org/blog"
                target="_blank"
                rel="noopener noreferrer"
                className="text-liberty-gold underline underline-offset-2 hover:opacity-80"
              >
                hawaiihealthcaretaskforce.org/blog
              </a>
              .
            </p>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default ResearchPage;
